/* eslint-disable import/no-deprecated */
import { classNames } from '@fullstory/util/fmt';
import React, { useEffect, useRef } from 'react';
import { Typography } from '../../deprecated/Typography';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import css from './index.module.scss';

export interface EditableTypographyProps
  extends Omit<React.ComponentProps<typeof Typography>, 'onChange' | 'className'> {
  testId?: string;
  /**
   * The display value. If an empty string is passed, the placeholder is displayed.
   */
  value: string;
  /**
   * A value to be displayed if the value is empty.
   */
  placeholder?: string;
  /**
   * The overflow width of the text. Defaults to "auto".
   */
  width?: React.ComponentProps<typeof Box>['width'];
  /**
   * A function that is called when the value is edited.
   */
  onChange: (value: string) => void;
  /**
   * A function that is called when the user is finished editing.
   */
  onSubmit: () => void;
  /**
   * A function that is called to validate user input.
   */
  validate?: (value: string) => boolean;
  /**
   * Whether the input text should be set to selected on focus
   */
  selectOnFocus?: boolean;
}

export const EditableTypography: React.FC<EditableTypographyProps> = ({
  testId = 'editable-typography',
  value,
  placeholder,
  width = 'auto',
  onChange,
  onSubmit,
  validate,
  selectOnFocus = false,
  children,
  ...rest
}) => {
  const [selectionRange, setSelectionRange] = React.useState<number[]>();
  const [focused, setFocused] = React.useState(false);

  return (
    <Box
      data-component="EditableTypography"
      testId={testId}
      className={classNames(css.wrapper, {
        [css.editable]: !focused,
        [css.focused]: focused,
      })}
      inline
      overflow="hidden"
      width={width}
      maxWidth={1}
      onClick={() => {
        if (focused) return;
        const selection = window.getSelection();
        if (selection && selection.anchorOffset !== selection.focusOffset) {
          // First priority - select a highlighted range
          setSelectionRange([selection.anchorOffset, selection.focusOffset]);
        } else if (selectOnFocus) {
          // Second priority - select the entire text if selectedOnFocus is set
          setSelectionRange([0, value.length]);
        } else if (selection) {
          // Last priority - select a cursor location (i.e. anchorOffset === focusOffset)
          setSelectionRange([selection.anchorOffset, selection.focusOffset]);
        }
        setFocused(true);
      }}
      {...identifyComponent('editable-typography')}
    >
      {/* hidden value to match the width of, &nbsp; added to solve for input sometimes scanning */}
      {focused && (
        <Typography {...rest} ellipsize tag="span" aria-hidden="true" className={css.hiddenText}>
          {value || placeholder}&nbsp;
        </Typography>
      )}
      <Box className={css.text} width={1}>
        <Typography
          {...rest}
          ellipsize
          tag="div"
          className={classNames(css.fullWidth, { [css.dim]: !value })}
        >
          {focused ? (
            <EditableTypographyInput
              value={value}
              placeholder={placeholder}
              selectionRange={selectionRange}
              onChange={onChange}
              onSubmit={() => {
                setFocused(false);
                setSelectionRange(undefined);
                onSubmit();
              }}
              onCancel={() => {
                setFocused(false);
                setSelectionRange(undefined);
              }}
              validate={validate}
            />
          ) : (
            <span>{value || placeholder}&nbsp;</span>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

interface EditableTypographyInputProps {
  value: string;
  placeholder?: string;
  selectionRange?: number[];
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  validate?: (value: string) => boolean;
}
const EditableTypographyInput: React.FC<EditableTypographyInputProps> = ({
  value,
  placeholder,
  selectionRange,
  onChange,
  onSubmit,
  onCancel,
  validate,
}) => {
  // store ref to original value
  const initialValueRef = useRef(value);
  const inputEl = useRef<HTMLInputElement>(null);

  const handleCancel = () => {
    // revert to original value
    onChange(initialValueRef.current);
    onCancel();
  };

  const handleSubmit = () => {
    if (value !== initialValueRef.current && (!validate || validate(value))) {
      // only submit if value has changed and is valid
      onSubmit();
    } else {
      // cancel if value has not changed
      handleCancel();
    }
  };

  useEffect(() => {
    // set the selection range if provided
    if (selectionRange != null) {
      const [anchorOffset, focusOffset] = selectionRange;
      if (anchorOffset != null && focusOffset != null) {
        const isBackward = anchorOffset > focusOffset;
        inputEl.current?.setSelectionRange(
          isBackward ? focusOffset : anchorOffset,
          isBackward ? anchorOffset : focusOffset,
          isBackward ? 'backward' : 'forward',
        );
      }
    }
    // focus input on mount
    inputEl.current?.focus();
  }, [selectionRange]);

  return (
    <input
      data-component="EditableTypographyInput"
      ref={inputEl}
      className={classNames(css.unstyledInput, css.fullWidth)}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onBlur={handleSubmit}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          handleCancel();
        }
        if (e.key === 'Enter') {
          handleSubmit();
        }
      }}
    />
  );
};
